import { z } from 'zod'

/**
 * The pro-ration configuration parameters Annex A stories 40–47 ask PSD to
 * administer.
 *
 * ## Why these are ONE closed list and not eight unrelated screens
 *
 * `docs/design/rule-governance.md` §6 already committed to this: *"Annex A
 * 26–36, 40–47 — parameter and reference-data management … They stop being a
 * dozen unrelated CRUD screens and become one governed capability."* A closed
 * union is what makes that true at the type level — every authorisation table,
 * every catalogue row and every route branch over these is exhaustive, so
 * adding a ninth parameter fails to compile in each place that has to decide
 * something about it rather than silently inheriting whatever the last one did.
 *
 * ## Why the names are PRS2's and not the story titles
 *
 * `docs/design/domain-language.md` is the vocabulary these have to join. The
 * tender's story titles ("CH (Child) Max Subsidised Length Management") are
 * headings, not terms; the names below are the terms, and the story each one
 * answers is recorded in `app.reference_parameter.story` and in
 * `docs/requirements/RTM.md` rather than smuggled into an identifier.
 *
 * ⚠ **`CH` means two different things in Annex A and they are two parameters
 * here.** Story 46 is `CH (Child)` — the maximum number of days the government
 * subsidises a dependent child's hospitalisation. Story 47 is
 * `CH (Community Hospitals)` — the 4-character service codes for community
 * hospital care. Same two letters, unrelated meanings, adjacent story numbers.
 * `domain-language.md` §2 exists for exactly this collision class, and
 * collapsing them under one `CH_` prefix would have been the first mistake
 * available here.
 */
export const REFERENCE_PARAMETERS = [
  /** Annex A 40 — NB / DA / UA, and the bill type each identifies. */
  'ADJUSTMENT_INDICATOR',
  /** Annex A 41 — the SGH-benchmarked pegged charge for a ward class, per HSP. */
  'PEGGED_WARD_RATE',
  /** Annex A 42 — the 6-character financial identifier an expense category routes to. */
  'ACCOUNT_CODE',
  /** Annex A 43 — the longest an MBIM record stays valid. */
  'MBIM_MAX_VALIDITY',
  /** Annex A 44 — the lowest ward class a service-injury case is entitled to. */
  'MIN_WARD_SERVICE_INJURY',
  /** Annex A 45 — the longest a bill may reach PRS after the date of medical service. */
  'MAX_BILL_GRACE_PERIOD',
  /** Annex A 46 — the most days of a child's hospitalisation the government subsidises. */
  'CH_CHILD_MAX_SUBSIDISED_LENGTH',
  /** Annex A 47 — which of the three community-hospital service types a code is. */
  'CH_SERVICE_TYPE',
  /**
   * Annex A 47 — whether a community-hospital service code attracts subsidy.
   *
   * ⚠ Deliberately SEPARATE from `CH_SERVICE_TYPE`, even though story 47 lists
   * the code, the type and the "subsidy eligibility flags" in one sentence.
   * They are two facts with two independent histories: a service can stop
   * attracting subsidy on a policy date without ceasing to be rehabilitation.
   * Carrying them on one row would force a new row — and therefore a new
   * effective window — for the type every time only the flag moved, and the
   * type's own history would then read as though it had changed when it had
   * not. Two series, two windows, each superseded on its own date.
   */
  'CH_SERVICE_SUBSIDY_ELIGIBILITY',
  /**
   * Annex A 41, the tenth parameter — who bears the amount a charged rate
   * carries above the pegged ward rate benchmark (Task 8).
   *
   * ⚠ **This is a CONTROL, not a policy PRS2 is asserting.** Story 41 says the
   * pegged rate "protects beneficiaries while allowing HSP operational
   * flexibility" and never says who pays the difference when an HSP charges
   * above the SGH benchmark. Inventing an answer would be inventing the
   * allocation of real money, which this project forbids (see
   * `docs/design/reference-data.md` §7's own argument for why the pegged rate
   * was left unwired through Task 7). So this parameter exists to make the gap
   * a governed, visible control instead of a silent guess: `013_seed_reference_data.sql`
   * seeds the CATALOGUE row and deliberately seeds NO VALUE in force, so a bill
   * that would trigger the cap is refused, naming this parameter, until PSD
   * authors one. Filed as clarification **Q-31** — this parameter is the
   * deliberate ABSENCE of an assumption, not one being registered.
   */
  'PEGGED_RATE_EXCESS_BEARER',
] as const

export type ReferenceParameter = (typeof REFERENCE_PARAMETERS)[number]

const KNOWN_PARAMETERS: ReadonlySet<string> = new Set<string>(REFERENCE_PARAMETERS)

/**
 * Narrow an arbitrary string to a parameter this system knows.
 *
 * ⚠ A type guard rather than a parse-or-throw, because the one caller that
 * matters is a URL segment: `GET /api/reference/:parameter` must answer an
 * unknown parameter with the SAME 404 it answers a parameter the caller may
 * not read. A throw there would be a distinguishable refusal, which is the
 * enumeration oracle every other route in this gateway is written to avoid.
 */
export function isReferenceParameter(value: string): value is ReferenceParameter {
  return KNOWN_PARAMETERS.has(value)
}

/**
 * A whole number of days.
 *
 * Three of these eight parameters are durations — the MBIM maximum validity
 * period (43), the maximum bill grace period (45) and the child maximum
 * subsidised length (46) — and every one of them is counted in whole days
 * against a **service date**, never in wall-clock time.
 *
 * ⚠ **The tender states no unit for any of the three.** Story 43 says "maximum
 * validity period durations", story 45 "maximum grace period durations",
 * story 46 "maximum subsidised day limits" — only the last names days. Days is
 * therefore PRS2's canonical unit and a month-expressed policy is converted at
 * authoring time, on the calendar, by the person who authors it. Recorded as
 * **A-12**, because a duration converted twice by two different rules is money.
 *
 * ⚠ Zero is legal and is not an accident. A grace period of zero days means the
 * bill must reach PRS on the day of service; a subsidised length of zero means
 * the government subsidises none of it. Both are policies PSD could set, and a
 * type that refused zero would force whoever wanted one to express it as a
 * missing row, which reads as "not configured" instead.
 */
declare const wholeDaysBrand: unique symbol

const wholeDaysSchema = z
  .number()
  .int('a duration must be a whole number of days')
  .min(0, 'a duration in days must not be negative')
  // 36,500 days is a hundred years. Not a policy bound and not presented as one
  // — it is the ceiling above which a value is certainly a data-entry defect
  // (a date pasted into a duration field, a millisecond count) rather than a
  // duration anybody meant. §14.6(c) requires a maximum; the tender states
  // none, so this one is ours and is registered as A-12.
  .max(36_500, 'a duration in days must not exceed 36500 (a hundred years)')

export const WholeDays = wholeDaysSchema.brand<typeof wholeDaysBrand>()
export type WholeDays = z.infer<typeof WholeDays>

/** Parse-or-throw, for rule authoring and for the edges where a raw number arrives. */
export const wholeDays = (value: number): WholeDays => WholeDays.parse(value)

/**
 * An account code — Annex A story 42's **6-character financial identifier**.
 *
 * ⚠ The length is the TENDER'S, not ours: story 42 says "6-character financial
 * identifiers" three times over, in the title, the goal and the benefit. That
 * makes it one of the very few field lengths the pack states at all, and
 * **P2C11 §14.6(c)** makes validating it a contractual obligation rather than
 * politeness. It is enforced in three places that must agree — here, in
 * `app.reference_parameter`'s length bounds, and by the trigger in
 * `012_reference_data.sql` that applies them — the same three-place discipline
 * A-11 records for the rejection reason.
 *
 * ⚠ **The character SET is ours and the length is not.** The tender says
 * "6-character" and nothing about which characters. Upper-case alphanumeric is
 * PRS2's assumption (**A-13**), taken because **P2C3 §7.1.2** delegates data
 * element formats to WOG data standards whose text is not furnished — its
 * parent §7.1.1 keeps the governing policy behind an on-site undertaking. The
 * risk is asymmetric in the safe direction: a WOG standard that permits MORE
 * characters is a trivial widening, and one that permits fewer would have been
 * violated by any looser rule we shipped in the meantime.
 */
declare const accountCodeBrand: unique symbol

export const AccountCode = z
  .string()
  .length(6, 'an account code is exactly 6 characters (Annex A story 42)')
  .regex(/^[A-Z0-9]{6}$/, 'an account code is upper-case alphanumeric (assumption A-13; no WOG code table in hand)')
  .brand<typeof accountCodeBrand>()
export type AccountCode = z.infer<typeof AccountCode>

export const accountCode = (value: string): AccountCode => AccountCode.parse(value)

/**
 * A community-hospital service code — Annex A story 47's **4-character** code.
 *
 * Same provenance as {@link AccountCode}: the length is the tender's ("4-character
 * service codes", stated in the goal and again in the first acceptance
 * criterion), the character set is A-13.
 *
 * ⚠ Story 47 also requires "MCPS code table validation" — validation against the
 * legacy system's own code table. **That table is not in the pack and cannot be
 * checked here.** The binding is marked `TODO(WOG-DATA-STD)` on the seed and is
 * registered as A-13; what this type enforces is shape, which is strictly less
 * than the story asks and is written that way in the RTM.
 */
declare const chServiceCodeBrand: unique symbol

export const ChServiceCode = z
  .string()
  .length(4, 'a community-hospital service code is exactly 4 characters (Annex A story 47)')
  .regex(/^[A-Z0-9]{4}$/, 'a service code is upper-case alphanumeric (assumption A-13; MCPS code table not furnished)')
  .brand<typeof chServiceCodeBrand>()
export type ChServiceCode = z.infer<typeof ChServiceCode>

export const chServiceCode = (value: string): ChServiceCode => ChServiceCode.parse(value)

/**
 * The adjustment indicators Annex A story 40 names: **NB**, **DA**, **UA**.
 *
 * New Bill, Downward Adjustment, Upward Adjustment. These are the tokens the
 * file-exchange path (stories 2–8) reads off an incoming record to decide which
 * of the three pro-ration scenarios applies, which is why story 40 exists at
 * all — it is the code table that wave consumes.
 *
 * ⚠ **A closed union, and the mapping to PRS2's own bill types lives in the
 * DATA, not here.** `app.reference_value` holds `NB → NEW`, `DA →
 * DOWNWARD_ADJUSTMENT`, `UA → UPWARD_ADJUSTMENT` as an effective-dated series,
 * so a fourth indicator, or a changed meaning for an existing one, is a
 * stewarded data change on a service-date window rather than a code change —
 * which is the whole point of story 40 being a *record management* story. What
 * is fixed in code is only that the token itself is one of three; what it
 * MEANS on a given date of medical service is looked up.
 */
export const ADJUSTMENT_INDICATORS = ['NB', 'DA', 'UA'] as const
export type AdjustmentIndicator = (typeof ADJUSTMENT_INDICATORS)[number]

const KNOWN_INDICATORS: ReadonlySet<string> = new Set<string>(ADJUSTMENT_INDICATORS)

/** Is this one of story 40's three indicators? */
export function isAdjustmentIndicator(value: string): value is AdjustmentIndicator {
  return KNOWN_INDICATORS.has(value)
}

/**
 * Who bears the amount a charged rate carries above the pegged ward rate
 * benchmark — `PEGGED_RATE_EXCESS_BEARER`'s own three permitted values
 * (Annex A 41, Task 8).
 *
 * ⚠ **Deliberately seeded with none of these in force.** See the parameter's
 * own docstring above: the tender never answers who bears the excess, and
 * PRS2 must not answer for it. `GOVERNMENT` and `OFFICER` are money the
 * pro-ration split can move (subsidy and co-payment are the only two buckets
 * `ProrationResult` has); `PROVIDER` names a real third policy the tender's
 * words permit but this pro-ration split has no mechanism to enact — moving
 * money out of what is paid to the Healthcare Service Provider is a
 * SETTLEMENT-level recovery instruction, which does not exist in PRS2 today.
 * `services/proration/src/pegged-ward-rate.ts` refuses `PROVIDER` for exactly
 * that reason rather than silently doing nothing with it.
 */
export const PEGGED_RATE_EXCESS_BEARERS = ['GOVERNMENT', 'OFFICER', 'PROVIDER'] as const
export type PeggedRateExcessBearer = (typeof PEGGED_RATE_EXCESS_BEARERS)[number]

const KNOWN_EXCESS_BEARERS: ReadonlySet<string> = new Set<string>(PEGGED_RATE_EXCESS_BEARERS)

/** Is this one of `PEGGED_RATE_EXCESS_BEARER`'s three permitted values? */
export function isPeggedRateExcessBearer(value: string): value is PeggedRateExcessBearer {
  return KNOWN_EXCESS_BEARERS.has(value)
}
